import React from "react";
import ReactDOM from "react-dom/client";

import { BrowserRouter } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import "./index.css";
import "./i18n"; // i18n initialization (must be before App)
import { installChunkErrorRecovery } from "./lib/chunkErrorRecovery";

import App from "./App";
import EmbeddedLicenseBanner from "./components/embedded/EmbeddedLicenseBanner";
import store from "./store";

import { Provider } from "react-redux";
import { ProSidebarProvider } from "react-pro-sidebar";
import { HelmetProvider } from "react-helmet-async";
import { AlertProvider } from "./contexts/AuthContext";

installChunkErrorRecovery();

const muiTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#09090b",
      paper: "#18181b",
    },
    text: {
      primary: "#fafafa",
      secondary: "#a1a1aa",
    },
    primary: { main: "#d4d4d8" },
    divider: "rgba(255, 255, 255, 0.08)",
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: [
      '"Outfit"',
      '"IBM Plex Sans"',
      "ui-sans-serif",
      "system-ui",
      "sans-serif",
    ].join(","),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Outfit", "IBM Plex Sans", sans-serif',
          backgroundColor: "#09090b",
          color: "#fafafa",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 500 },
      },
    },
  },
});

const rootEl = document.getElementById("root")!;

const tree = (
  <React.StrictMode>
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <ProSidebarProvider>
        <BrowserRouter>
          <Provider store={store}>
            <HelmetProvider>
              <AlertProvider>
                {/* Renders only on an Atomizer Embedded device. */}
                <EmbeddedLicenseBanner />
                <App />
              </AlertProvider>
            </HelmetProvider>
          </Provider>
        </BrowserRouter>
      </ProSidebarProvider>
    </ThemeProvider>
  </React.StrictMode>
);

// The server injects crawlable content for some routes (#seo-ssr, see
// server/api.py:_render_spa) OUTSIDE #root. Remove it once the SPA takes
// over. #root is always mounted fresh: react-snap output (which served the
// home page's markup on every route) is no longer used, so hydrating
// would only ever produce mismatches.
document.getElementById("seo-ssr")?.remove();
ReactDOM.createRoot(rootEl).render(tree);
